import React, { useCallback, useEffect } from 'react';
import { IView, Link } from '@framework-controls/core';
import { strings } from 'src/localization';
import { Customer, RepairOrder, SpecData, UpdateApptVehInp, VehicleRec, ascCtaInteractionInput, ascPageviewInput } from 'src/models';
import { ViewMode } from 'src/enums';
import { IVehicleState, IRepairOrdersState } from 'src/store/states';
import * as css from './OpenRepairSelect.module.scss';
import { ComponentMapIds } from '../map';
import ScrollBar from '../ScrollBar/ScrollBar';
import { ascCtaInteraction, ascPageview, getElementColor } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';
import { getProviderId, sendSdEvent } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';

export interface IProps {
    viewMode?: ViewMode;
    repairOrders: IRepairOrdersState;
    customer: Customer;
    vehicles: IVehicleState;
    mainPaneViewOrder: IView[];
    locale: string;
    theme: string;
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    identityVerified: boolean;
    pushToMainPanes: (inputView: IView) => void;
    selectRO: (roNum: string) => void;
    selectVehicle: (id: string) => void;
    getROHistory: () => void;
    clearROHistory: () => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    updateApptVeh: (inp: UpdateApptVehInp) => void;
    clearAdditionalServices: () => void;
}

const OpenRepairSelect = (props: IProps) => {
    const isSDAnalyticsEnabled =
        props.specData?.toyotaTrackingFlag === 'Y' ||
        props.specData?.lexusTrackingFlag === 'Y' ||
        props.specData?.mbzTrackingFlag === 'Y' ||
        props.specData?.mazdaCATrackingFlag === 'Y' ||
        props.specData?.vwTrackingFlag === 'Y';

    useEffect(() => {
        if (props.locale === 'xx') {
            // setForgotCSS(css.passLitAccess);

        }
    }, [props.locale]);

    useEffect(() => {
        const currentPane = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1];
        if (currentPane.viewId === ComponentMapIds.OpenRepairSelect) {
            if (isSDAnalyticsEnabled) {
                sendSdEvent('scheduleServiceVehicleShown', {
                    scheduleServiceSessionId: props.sessionId,
                    scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched,
                        props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
                });
            }
        }
    }, [props.mainPaneViewOrder, props.sessionId, props.specData, props.toyotaMCLaunched,
        props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched, isSDAnalyticsEnabled]);

    useEffect(() => {
        props.addLogEntry('Screen Entry', 'OpenRepairSelect', '');
    }, [
        props.addLogEntry,
    ]);
    let repairOrders: RepairOrder[] = props.repairOrders.RepairOrders;
    // If we select an RO, make sure to select the vehicle as well and then push VehLayout to the main pane
    const selectRepairOrder = useCallback((roNum: string) => {
        const ro = repairOrders.find(repairOrder => repairOrder.RONum === roNum);
        if (ro) {
            const vehID = props.vehicles.vehicles.find(veh => veh.vin === ro.vin)?.id;
            props.selectRO(ro.RONum);
            if (vehID) {
                props.selectVehicle(vehID);
                const apptInp: UpdateApptVehInp = {
                    vin: props.vehicles.vehicles[vehID].vin,
                    makeCode: props.vehicles.vehicles[vehID].makeCode,
                    make: props.vehicles.vehicles[vehID].make,
                    model: props.vehicles.vehicles[vehID].model,
                    series: props.vehicles.vehicles[vehID].model,
                    vehYr: props.vehicles.vehicles[vehID].year,
                    vehColor: props.vehicles.vehicles[vehID].color,
                    mileage: props.vehicles.vehicles[vehID].prevMileage,
                    imagePath: props.vehicles.vehicles[vehID].imagePath,
                };
                props.updateApptVeh(apptInp);
            }
            props.clearAdditionalServices();
            props.clearROHistory();
            props.getROHistory();
            props.pushToMainPanes({
                viewId: ComponentMapIds.VehLayout,
                component: ComponentMapIds.VehLayout,
            });
        }
        const elementColor = getElementColor(roNum+'link', 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: roNum,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'selection_link',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, roNum+'link', props.vehicles.vehicles, props.vehicles.selectedVehicle);
        if (isSDAnalyticsEnabled) {
            sendSdEvent('scheduleServiceVehicleStart', {
                scheduleServiceSessionId: props.sessionId,
                scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched,
                    props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
            });
        }
    }, [
        props.pushToMainPanes, props.selectRO, repairOrders, props.selectVehicle,
        props.vehicles, props.clearROHistory, props.getROHistory, props.updateApptVeh,
        props.clearAdditionalServices, props.sessionId, props.specData, props.toyotaMCLaunched,
        props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched, isSDAnalyticsEnabled]);

    useEffect(() => {
        const viewLength = props.mainPaneViewOrder.length;
        // If we only have one repairOrder on the list, select the repair order. If it's already selected show VehLayout
        // Only push the VehLayout component if we are currently on OpenRepairSelect
        if (repairOrders?.length === 1 && props.mainPaneViewOrder[viewLength - 1].component === ComponentMapIds.OpenRepairSelect) {
            if (props.repairOrders.currentRepairOrder === repairOrders[0].RONum) {
                props.clearROHistory();
                props.getROHistory();
                props.pushToMainPanes({
                    viewId: ComponentMapIds.VehLayout,
                    component: ComponentMapIds.VehLayout,
                });
            } else {
                selectRepairOrder(repairOrders[0].RONum);
            }
        }
    }, [repairOrders, selectRepairOrder, props.repairOrders.currentRepairOrder, props.mainPaneViewOrder,
        props.clearROHistory, props.getROHistory, props.pushToMainPanes]);


    useEffect(() => {
        if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.OpenRepairSelect &&
            !props.repairOrders.loadingROs && props.repairOrders.RepairOrders.length > 1) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: repairOrders?.length.toString(),
            };
            ascPageview(ascPageViewInp, props.vehicles.vehicles, props.vehicles.selectedVehicle);
        }
    }, [props.mainPaneViewOrder, repairOrders?.length, props.repairOrders.loadingROs,
        props.vehicles.vehicles, props.vehicles.selectedVehicle, props.repairOrders.RepairOrders]);

    const headRowStyles = classNames(
        css.Headrow,
        {
            [css.HeadrowBMW]: props.theme === 'bmw',
            [css.HeadrowFord]: props.theme === 'ford',
            [css.HeadrowIN]: props.theme === 'infiniti',

        },
    );

    if (repairOrders?.length > 0) {
        let roList: (JSX.Element | null)[] = [];
        // Filter repair orders by selected vehicle if one is selected
        let selectedVehicle: VehicleRec | undefined = undefined;
        selectedVehicle = props.vehicles.vehicles.find(vehicle => vehicle.id === props.vehicles.selectedVehicle);
        if (selectedVehicle && repairOrders?.length > 0) {
            repairOrders = repairOrders.filter(repairOrder => repairOrder.vin === selectedVehicle?.vin);
        }

        // List each repair order and find its vehicle information
        roList = repairOrders.map((ro, index) => {
            const vehicle = props.vehicles.vehicles.find(vehicle => vehicle.vin === ro.vin);
            if (vehicle) {
                let vehMake = '';
                if (vehicle?.makeCode === '2I') {
                    vehMake = 'Scion';
                } else {
                    vehMake = vehicle?.make ?? '';
                }

                const linkDivStyles = classNames(
                    css.linkDiv,
                    {
                        [css.linkDivBMW]: props.theme === 'bmw',

                    },
                );

                return (
                    <div className={css.row} key={index}>
                        <p className={css.vehicle}>
                            {vehicle.year}{' '}{vehMake}{' '}{vehicle.model}
                        </p>
                        <p className={css.openDate}>
                            {ro.RODate}
                        </p>
                        <p className={css.repairNumber}>
                            <Link
                                className={linkDivStyles}
                                key={ro.RONum}
                                id={ro.RONum + 'link'}
                                onClick={() => selectRepairOrder(ro.RONum)}
                            >
                                {ro.RONum}
                            </Link>
                            <span className={css.linkSpace}></span>
                        </p>
                    </div>
                );
            }

            return null;
        });

        return (
            <ScrollBar scrollVertical>
                <div className={css.mainDiv}>
                    <h2 className={
                        props.theme === 'mazda' ? css.headerMazda :
                            props.theme === 'genesis' ? css.headerGenesis :
                                undefined
                    }>{props.identityVerified ? props.customer.firstName + ',' : strings.WelcomeBack}</h2>
                    <h3>{strings.MultipleRepairs}</h3>
                    <div className={css.ROGrid}>
                        <div className={headRowStyles}>
                            <div className={css.vehicle}>{strings.Vehicle}</div>
                            <div className={css.openDate}>{strings.RepairDate}</div>
                            <div className={css.repairNumberTitle}>{strings.RepairNumber}</div>
                        </div>
                        {roList}
                    </div>
                </div>
            </ScrollBar>
        );
    }

    return (
        <div className={css.mainDiv}>
            <h2 className={
                props.theme === 'mazda' ? css.headerMazda :
                    props.theme === 'genesis' ? css.headerGenesis :
                        undefined
            }>{props.identityVerified ? props.customer.firstName + ',' : strings.WelcomeBack}</h2>
            <h3>{strings.NoRepairs}</h3>
        </div>
    );
};


export default OpenRepairSelect;
